import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Fragment, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Box, Row, TextButton, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import AddFilter from '~/components/TableFilters/AddFilter/AddFilter';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { deriveNameFilterOptions } from '../../AgreementsDashboard.utils';
import { DEFAULT_AGREEMENT_FILTER, isDefaultAgreementFilter } from '../agreementFilterDefaults';
import { useAgreementsServiceGrammar } from '../agreementsServiceGrammar';
import { useAgreementsAiSearchStore } from '../hooks/useAgreementsAiSearchStore';
import { ADDABLE_FILTER_GROUPS, ADDABLE_FILTER_KEYS, addEmptyFilter, isAddableFilterPresent } from './agreementAddFilter';
import { buildAgreementPredicateChipUnits, buildPredicateCallbacks } from './AgreementPredicateChips.utils';
import { useAgreementsBundlesGrammar } from './agreementsCollectionGrammar';
import { useAgreementSettingsGrammar } from './agreementSettingsGrammar';
export const GetAgreementPredicateChipsTID = (parentTestId)=>getTestIds(parentTestId);
const CHIP_KEY_BY_ADDABLE_FILTER = {
    status: 'status',
    paymentMethod: 'paymentMethod',
    amendmentStatus: 'amendmentStatus',
    hasCredit: 'hasCredit',
    effectiveDate: 'effectiveDate',
    approvalDate: 'approvalDate',
    lastBilled: 'lastBilled',
    occurrence: 'billingOccurrence',
    automaticServices: 'automaticState',
    serviceName: 'name',
    serviceStatus: 'completionState',
    billingTrigger: 'billingTrigger'
};
export const AgreementPredicateChips = (param)=>{
    let { testId, serviceNames, agreements, isVendor, matchCount, showApprovalDateFilter, showAmendmentStatusFilter } = param;
    const { AgreementPredicateChipsTestIds } = useMemo(()=>GetAgreementPredicateChipsTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys('agreement');
    const { t: commonFilter } = useLocaleKeys('common.filter');
    const filter = useAgreementsAiSearchStore((s)=>s.filter);
    const editFilter = useAgreementsAiSearchStore((s)=>s.editFilter);
    const setFilter = useAgreementsAiSearchStore((s)=>s.setFilter);
    const { setOrArm, clearGroup, toggleConnector } = useAgreementsAiSearchStore(useShallow((s)=>({
            setOrArm: s.setOrArm,
            clearGroup: s.clearGroup,
            toggleConnector: s.toggleConnector
        })));
    const [autoOpenKey, setAutoOpenKey] = useState(null);
    const serviceGrammar = useAgreementsServiceGrammar(serviceNames);
    const settingsGrammar = useAgreementSettingsGrammar();
    const collectionGrammar = useAgreementsBundlesGrammar();
    useOnMount(()=>{
        if (useAgreementsAiSearchStore.getState().filter == null) setFilter(DEFAULT_AGREEMENT_FILTER, '');
    });
    const nameOptions = useMemo(()=>deriveNameFilterOptions(agreements, isVendor), [
        agreements,
        isVendor
    ]);
    const activeFilter = filter !== null && filter !== void 0 ? filter : {
        version: '1'
    };
    const statusOptions = [
        {
            value: 'active',
            label: t.filters.statusOptions.active()
        },
        {
            value: 'exhausted',
            label: t.filters.statusOptions.completed()
        },
        {
            value: 'terminated',
            label: t.filters.statusOptions.terminated()
        }
    ];
    const paymentMethodOptions = [
        {
            value: 'credit',
            label: t.filters.paymentMethodOptions.card()
        },
        {
            value: 'bank',
            label: t.filters.paymentMethodOptions.bankAccount()
        },
        {
            value: 'verificationInitiated',
            label: t.filters.paymentMethodOptions.manualVerificationInitiated()
        },
        {
            value: 'verificationFailed',
            label: t.filters.paymentMethodOptions.manualVerificationFailed()
        },
        {
            value: 'notSet',
            label: t.filters.paymentMethodOptions.notSet()
        }
    ];
    const amendmentStatusOptions = [
        {
            value: 'amendmentDraft',
            label: t.filters.amendmentsStatusOptions.amendmentDraft()
        },
        {
            value: 'awaitingApproval',
            label: t.filters.amendmentsStatusOptions.awaitingApproval()
        },
        {
            value: 'noChanges',
            label: t.filters.amendmentsStatusOptions.noChanges()
        }
    ];
    const hasCreditOptions = [
        {
            value: 'yes',
            label: t.filters.hasCreditOptions.hasCredit()
        },
        {
            value: 'no',
            label: t.filters.hasCreditOptions.none()
        }
    ];
    const testIds = {
        Status: AgreementPredicateChipsTestIds.Status(),
        PaymentMethod: AgreementPredicateChipsTestIds.PaymentMethod(),
        AmendmentStatus: AgreementPredicateChipsTestIds.AmendmentStatus(),
        HasCredit: AgreementPredicateChipsTestIds.HasCredit(),
        EffectiveDate: AgreementPredicateChipsTestIds.EffectiveDate(),
        ApprovalDate: AgreementPredicateChipsTestIds.ApprovalDate(),
        ClientName: AgreementPredicateChipsTestIds.ClientName(),
        AgreementTitle: AgreementPredicateChipsTestIds.AgreementTitle(),
        ContactName: AgreementPredicateChipsTestIds.ContactName(),
        Assignee: AgreementPredicateChipsTestIds.Assignee(),
        ModifiedBy: AgreementPredicateChipsTestIds.ModifiedBy(),
        LastBilled: AgreementPredicateChipsTestIds.LastBilled(),
        Services: AgreementPredicateChipsTestIds.Services(),
        BundleName: AgreementPredicateChipsTestIds.BundleName(),
        OrGroup: AgreementPredicateChipsTestIds.OrGroup(),
        Settings: AgreementPredicateChipsTestIds.Settings()
    };
    const callbacks = buildPredicateCallbacks(activeFilter, (next)=>editFilter(_object_spread({
            version: activeFilter.version
        }, next)));
    const texts = {
        labels: {
            status: t.filters.labels.agreementStatus(),
            paymentMethod: t.filters.labels.paymentMethod(),
            amendmentStatus: t.filters.labels.amendmentsStatus(),
            hasCredit: t.aiSearch.filterLabels.hasCredit(),
            effectiveDate: t.filters.labels.effectiveDate(),
            approvalDate: t.filters.labels.approvalDate(),
            clientName: t.filters.labels.clientName(),
            agreementTitle: t.filters.labels.agreementTitle(),
            contactName: t.filters.labels.contactName(),
            assignee: t.filters.labels.assignee(),
            lastBilled: t.filters.labels.lastBillingDate(),
            occurrence: serviceGrammar.texts.servicesBillingOccurrenceLabel(),
            automaticServices: t.filters.labels.automaticServices(),
            serviceName: serviceGrammar.texts.servicesLabel(),
            serviceStatus: t.filters.labels.completion(),
            billingTrigger: serviceGrammar.texts.servicesBillingTriggerLabel(),
            bundleName: t.filters.labels.bundleNames()
        },
        options: {
            status: statusOptions,
            paymentMethod: paymentMethodOptions,
            amendmentStatus: amendmentStatusOptions,
            hasCredit: hasCreditOptions,
            clientName: nameOptions.clientName,
            agreementTitle: nameOptions.agreementTitle,
            contactName: nameOptions.contactName,
            assignee: nameOptions.assignee
        },
        modifiedBy: {
            label: t.dashboard.columnHeaders.lastModifiedBy(),
            me: serviceGrammar.texts.modifiedByValueMe(),
            anchorAi: serviceGrammar.texts.modifiedByValueAnchorAi()
        },
        bundleNoun: ()=>t.filters.bundlesNoun(),
        applyText: commonFilter.apply(),
        selectAllText: commonFilter.selectAll(),
        setValue: {
            all: t.aiSearch.value.all(),
            count: (count)=>t.aiSearch.value.count({
                    count
                })
        },
        dateRange: {
            allTime: t.filters.dateRange.allTime(),
            specificRange: t.filters.dateRange.specificRange(),
            from: t.filters.dateRange.from(),
            to: t.filters.dateRange.to()
        },
        lastBilled: {
            all: t.filters.lastBillingDateOptions.all(),
            allChip: t.aiSearch.value.all(),
            everBilled: t.filters.lastBillingDateOptions.everBilled(),
            neverBilled: t.filters.lastBillingDateOptions.neverBilled(),
            specificRange: t.filters.lastBillingDateOptions.specificRange(),
            from: t.filters.dateRange.from(),
            to: t.filters.dateRange.to()
        }
    };
    const units = buildAgreementPredicateChipUnits({
        predicate: activeFilter,
        testIds,
        callbacks,
        groupActions: {
            onArmChange: setOrArm,
            onClearGroup: clearGroup,
            onToggleConnector: toggleConnector
        },
        texts,
        serviceGrammar,
        settingsGrammar,
        collectionGrammar,
        autoOpenKey
    });
    const addFilterLabels = {
        status: texts.labels.status,
        paymentMethod: texts.labels.paymentMethod,
        amendmentStatus: texts.labels.amendmentStatus,
        hasCredit: texts.labels.hasCredit,
        effectiveDate: texts.labels.effectiveDate,
        approvalDate: texts.labels.approvalDate,
        lastBilled: texts.labels.lastBilled,
        occurrence: texts.labels.occurrence,
        automaticServices: texts.labels.automaticServices,
        serviceName: texts.labels.serviceName,
        serviceStatus: texts.labels.serviceStatus,
        billingTrigger: texts.labels.billingTrigger
    };
    const groupLabels = {
        agreements: t.filters.groupLabels.agreements(),
        services: t.filters.groupLabels.services()
    };
    const offeredKeys = ADDABLE_FILTER_KEYS.filter((key)=>(key !== 'approvalDate' || showApprovalDateFilter) && (key !== 'amendmentStatus' || showAmendmentStatusFilter));
    const addFilterGroups = ADDABLE_FILTER_GROUPS.map((group)=>({
            label: groupLabels[group.group],
            filters: group.keys.filter((key)=>offeredKeys.includes(key))
        }));
    const addedKeys = offeredKeys.filter((key)=>isAddableFilterPresent(activeFilter, key));
    const hasAppliedFilter = !isDefaultAgreementFilter(activeFilter);
    return /*#__PURE__*/ _jsx(Box, {
        testId: AgreementPredicateChipsTestIds.Root(),
        children: /*#__PURE__*/ _jsxs(Row, {
            alignItems: "center",
            gap: 8,
            wrap: true,
            children: [
                units.map((unit)=>/*#__PURE__*/ _jsx(Fragment, {
                        children: unit.node
                    }, unit.key)),
                /*#__PURE__*/ _jsx(AddFilter, {
                    testId: AgreementPredicateChipsTestIds.AddFilter(),
                    filtersMap: addFilterLabels,
                    selectedFilters: addedKeys,
                    onSelect: (key)=>{
                        setAutoOpenKey(CHIP_KEY_BY_ADDABLE_FILTER[key]);
                        editFilter(addEmptyFilter(activeFilter, key));
                    },
                    groups: addFilterGroups
                }),
                hasAppliedFilter && /*#__PURE__*/ _jsxs(Row, {
                    alignItems: "center",
                    gap: 4,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            testId: AgreementPredicateChipsTestIds.Count(),
                            skin: "secondary",
                            variant: "paragraph2",
                            children: t.dashboard.aiSearch.count({
                                count: matchCount
                            })
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            skin: "secondary",
                            variant: "paragraph2",
                            children: "•"
                        }),
                        /*#__PURE__*/ _jsx(TextButton, {
                            testId: AgreementPredicateChipsTestIds.ClearButton(),
                            bi: {
                                name: 'agreements_ai_search_clear'
                            },
                            onClick: ()=>setFilter(DEFAULT_AGREEMENT_FILTER, ''),
                            variant: "paragraph2",
                            underline: "none",
                            weight: "normal",
                            children: t.dashboard.aiSearch.clearButton()
                        })
                    ]
                })
            ]
        })
    });
};
